import { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table columns ──────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'date', label: 'Date' },
  { id: 'reference', label: 'Reference' },
  { id: 'particulars', label: 'Particulars' },
  { id: 'vchType', label: 'Vch Type' },
  { id: 'debit', label: 'Debit (₹)' },
  { id: 'credit', label: 'Credit (₹)' },
  { id: 'balance', label: 'Balance (₹)' }
];

const ACCOUNT_TYPES = [
  { value: 'cash', label: 'Cash Account' },
  { value: 'bank', label: 'Bank Account' },
  { value: 'debtor', label: 'Debtors Account' },
  { value: 'creditor', label: 'Creditors Account' },
  { value: 'expense', label: 'Expense Account' },
  { value: 'income', label: 'Income Account' }
];

const MOCK_LEDGER_ROWS = [
  {
    id: 1,
    date: '2024-04-01',
    reference: 'OB-00',
    particulars: 'Opening Balance',
    vchType: 'Opening',
    accountType: 'bank',
    month: 4,
    debit: 0,
    credit: 0,
    balance: 250000
  },
  {
    id: 2,
    date: '2024-04-05',
    reference: 'RCPT-101',
    particulars: 'HDFC Bank Payout',
    vchType: 'Receipt',
    accountType: 'bank',
    month: 4,
    debit: 0,
    credit: 120000,
    balance: 370000
  },
  {
    id: 3,
    date: '2024-04-08',
    reference: 'PYMT-202',
    particulars: 'Office Rent Payment',
    vchType: 'Payment',
    accountType: 'expense',
    month: 4,
    debit: 45000,
    credit: 0,
    balance: 325000
  },
  {
    id: 4,
    date: '2024-04-12',
    reference: 'PYMT-203',
    particulars: 'Software License',
    vchType: 'Payment',
    accountType: 'expense',
    month: 4,
    debit: 12500,
    credit: 0,
    balance: 312500
  },
  {
    id: 5,
    date: '2024-05-15',
    reference: 'RCPT-102',
    particulars: 'Commission Received',
    vchType: 'Receipt',
    accountType: 'income',
    month: 5,
    debit: 0,
    credit: 85000,
    balance: 397500
  },
  {
    id: 6,
    date: '2024-05-20',
    reference: 'JNL-301',
    particulars: 'Depreciation Entry',
    vchType: 'Journal',
    accountType: 'expense',
    month: 5,
    debit: 8000,
    credit: 0,
    balance: 389500
  },
  {
    id: 7,
    date: '2024-06-25',
    reference: 'PYMT-204',
    particulars: 'Staff Salary',
    vchType: 'Payment',
    accountType: 'expense',
    month: 6,
    debit: 75000,
    credit: 0,
    balance: 314500
  },
  {
    id: 8,
    date: '2024-06-30',
    reference: 'RCPT-103',
    particulars: 'Client Invoice Receipt',
    vchType: 'Receipt',
    accountType: 'debtor',
    month: 6,
    debit: 0,
    credit: 200000,
    balance: 514500
  }
];

const FY_OPTIONS = ['2024-25', '2023-24', '2022-23'];
const MONTH_OPTIONS = [
  { value: '', label: 'All Months' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' }
];

const getKPIs = (rows) => {
  const totalDr = rows.reduce((s, r) => s + (r.debit || 0), 0);
  const totalCr = rows.reduce((s, r) => s + (r.credit || 0), 0);
  return [
    {
      label: 'Opening Balance',
      value: 250000,
      icon: SavingsIcon,
      iconBg: '#eff6ff',
      iconColor: '#1d4ed8',
      border: '#1d4ed8'
    },
    {
      label: 'Total Debit (Outflow)',
      value: totalDr,
      icon: ArrowDownwardIcon,
      iconBg: '#fff5f5',
      iconColor: '#dc2626',
      border: '#dc2626'
    },
    {
      label: 'Total Credit (Inflow)',
      value: totalCr,
      icon: ArrowUpwardIcon,
      iconBg: '#f0fdf4',
      iconColor: '#16a34a',
      border: '#16a34a'
    },
    {
      label: 'Closing Balance',
      value: 514500,
      icon: AccountBalanceWalletIcon,
      iconBg: '#f0f9ff',
      iconColor: '#0891b2',
      border: '#4fbbc8',
      highlight: true
    }
  ];
};

export default function Ledger() {
  // filter state
  const [filterName, setFilterName] = useState('');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [vchType, setVchType] = useState(' ');
  const [accountType, setAccountType] = useState(' ');
  const [txnType, setTxnType] = useState(' ');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('date');

  // export menu
  const [exportAnchor, setExportAnchor] = useState(null);

  // download dialog
  const [dlOpen, setDlOpen] = useState(false);
  const [dlYear, setDlYear] = useState('2024-25');
  const [dlMonth, setDlMonth] = useState('');

  const handleRequestSort = (_, property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = useMemo(() => {
    let data = [...MOCK_LEDGER_ROWS];
    if (filterName)
      data = data.filter(
        (r) =>
          r.reference.toLowerCase().includes(filterName.toLowerCase()) ||
          r.particulars.toLowerCase().includes(filterName.toLowerCase())
      );
    if (vchType !== ' ') data = data.filter((r) => r.vchType === vchType);
    if (accountType !== ' ') data = data.filter((r) => r.accountType === accountType);
    if (txnType === 'credit') data = data.filter((r) => r.credit > 0);
    if (txnType === 'debit') data = data.filter((r) => r.debit > 0);
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, vchType, accountType, txnType, order, orderBy]);

  const kpis = getKPIs(MOCK_LEDGER_ROWS);
  const isNotFound = filtered.length === 0;

  const handleExport = (type) => {
    setExportAnchor(null);
    console.log('Export Ledger as', type);
  };

  const handleDownload = () => {
    // TODO: replace with API call — pass dlYear + dlMonth
    const monthLabel = MONTH_OPTIONS.find((m) => m.value === dlMonth)?.label || 'All Months';
    const rows =
      dlMonth !== '' ? MOCK_LEDGER_ROWS.filter((r) => r.month === dlMonth) : MOCK_LEDGER_ROWS;
    const csv = [
      'Date,Reference,Particulars,Vch Type,Debit,Credit,Balance',
      ...rows.map(
        (r) =>
          `${r.date},${r.reference},${r.particulars},${r.vchType},${r.debit},${r.credit},${r.balance}`
      )
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger_${dlYear}_${monthLabel.replace(/\s/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setDlOpen(false);
  };

  return (
    <Box>
      {/* ── 4 KPI Cards ── */}
      <Grid container spacing={2} mb={3}>
        {kpis.map((kpi) => {
          const IconComp = kpi.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={kpi.label}>
              <Card
                sx={{
                  p: 2.5,
                  borderLeft: `4px solid ${kpi.border}`,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    bgcolor: kpi.iconBg,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComp sx={{ color: kpi.iconColor, fontSize: '1.4rem' }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, color: '#718096', fontWeight: 500, display: 'block' }}
                  >
                    {kpi.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      fontSize: 17,
                      mt: 0.2,
                      color: kpi.highlight ? '#0891b2' : '#1e293b'
                    }}
                  >
                    ₹ {kpi.value.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card>
        {/* ── Collapsible Filter Panel ── */}
        {isOpenFilter && (
          <Box
            px={3}
            pt={2.5}
            pb={2.5}
            sx={{ borderBottom: '1px solid #eaedf1', bgcolor: '#fafbfc' }}
          >
            <Grid container spacing={2} alignItems="flex-end">
              {/* Account Type */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Account Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Accounts</MenuItem>
                    {ACCOUNT_TYPES.map((a) => (
                      <MenuItem key={a.value} value={a.value} sx={{ fontSize: 13 }}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Transaction Type */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Transaction Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={txnType}
                    onChange={(e) => setTxnType(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Types</MenuItem>
                    <MenuItem value="debit" sx={{ fontSize: 13 }}>
                      Debit Only
                    </MenuItem>
                    <MenuItem value="credit" sx={{ fontSize: 13 }}>
                      Credit Only
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Voucher Type */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Voucher Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={vchType}
                    onChange={(e) => setVchType(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All</MenuItem>
                    <MenuItem value="Receipt">Receipt</MenuItem>
                    <MenuItem value="Payment">Payment</MenuItem>
                    <MenuItem value="Journal">Journal</MenuItem>
                    <MenuItem value="Contra">Contra</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Reset */}
              <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Tooltip title="Reset All Filters">
                  <IconButton
                    size="small"
                    className="squareIconButton"
                    onClick={() => {
                      setAccountType(' ');
                      setTxnType(' ');
                      setVchType(' ');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          {/* LEFT — Export icon + Download icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Export */}
            {/* <Tooltip title="Export data" placement="top" arrow>
                            <IconButton className="squareIconButton" onClick={(e) => setExportAnchor(e.currentTarget)}>
                                <MdOutlineSystemUpdateAlt size={18} />
                            </IconButton>
                        </Tooltip> */}
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>

            {/* Download — opens Year + Month dialog */}
            <Tooltip title="Download Ledger" placement="top" arrow>
              <IconButton className="squareIconButton" onClick={() => setDlOpen(true)}>
                <DownloadIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* RIGHT — Search + Filter toggle */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search reference, particulars…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filters">
              <IconButton className="squareIconButton" onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={filtered.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filtered.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell>{row.date}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600, color: '#0d4689' }}>
                      {row.reference}
                    </StyledTableCell>
                    <StyledTableCell>{row.particulars}</StyledTableCell>
                    <StyledTableCell>{row.vchType}</StyledTableCell>
                    <StyledTableCell>
                      {row.debit !== 0 ? (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>
                          ₹ {row.debit.toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>–</span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.credit !== 0 ? (
                        <span style={{ color: '#16a34a', fontWeight: 600 }}>
                          ₹ {row.credit.toLocaleString()}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>–</span>
                      )}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }}>
                      ₹ {row.balance.toLocaleString()}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={7} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>

      {/* ═══════════════════════════════════════════════════════
               DOWNLOAD LEDGER DIALOG — select Year + Month
            ═══════════════════════════════════════════════════════ */}
      <Dialog open={dlOpen} onClose={() => setDlOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Download Ledger
          </Typography>
          <IconButton size="small" onClick={() => setDlOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <Box pt={1}>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
              Select the Financial Year and Month to download the ledger data as a PDF.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Financial Year
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={dlYear}
                    onChange={(e) => setDlYear(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {FY_OPTIONS.map((fy) => (
                      <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                        FY {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={dlMonth}
                    onChange={(e) => setDlMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTH_OPTIONS.map((m) => (
                      <MenuItem key={String(m.value)} value={m.value} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={() => setDlOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="mainBtn"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
